import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { createUserForInstitution } from '@adornis/buchungsheld-db/api/users.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-dropdown-search-selection';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-input.js';
import { addRoleToUser } from '@adornis/users/db/a-user.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { firstValueFrom } from 'rxjs';
import './participation-modify/components/bh-participation-modify-actions.js';

@customElement('bh-prompt-user-picker')
export class BHPromptUserPicker extends TrDialog<Maybe<User>> {
  @property({ attribute: false }) additionalFieldsForUser: PartialEntityData<User> = {};
  @property({ attribute: false }) institutionID: Maybe<string>;
  @property({ attribute: false }) userTypeForNewlyCreatedUser: Maybe<string>;

  static override get element_name() {
    return 'bh-prompt-user-picker';
  }

  @state() protected _isEditMode = false;
  @state() protected _form = new FormController(this, new User({}), {
    success: async instance => this.addUser(instance),
  });

  private lastSearch = '';

  override content() {
    return html` <x-flex space="md"> ${this._isEditMode ? this.modifyUser() : this.chooseUser()} </x-flex> `;
  }

  protected modifyUser() {
    if (!this._form.document) throw new Error('user should be set by now');
    return html`
      <x-flex space="lg">
        <x-flex horizontal>
          <x-button mode="text" icon="west" @click=${() => (this._isEditMode = false)}> Zurück zur Auswahl </x-button>
        </x-flex>
        <x-flex space="sm">
          <x-input placeholder="E-Mail" ${this._form.field('email')}></x-input>
          <x-input placeholder="Vorname" ${this._form.field('firstName')}></x-input>
          <x-input placeholder="Nachname" ${this._form.field('lastName')}></x-input>
        </x-flex>
        <x-flex horizontal crossaxis-center space-between space="md">
          <x-button mode="outline" icon="close" @click=${() => this.close(null)}> Abbrechen </x-button>
          <x-button icon="save" @click=${() => this._form.validate()}> Speichern </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  protected chooseUser() {
    return html`
      <tr-dropdown-search-selection
        empty-state-with-search=${this._translation.translate('dropdowns', 'emptyStateUsers')}
        empty-state-without-search=${this._translation.translate('dropdowns', 'emptyStateUsersWithoutSearch')}
        placeholder="Nutzer auswählen"
        @value-picked=${(e: ValueEvent<User>) => {
          return this._switchToEditMode(e.detail.value);
        }}
        .renderString=${doc => (doc === null ? '+ Nutzer anlegen ' : doc?.email ?? '')}
        .search=${async search => {
          this.lastSearch = search ?? '';
          const users = await firstValueFrom(
            User.subscribeUsersPaginated(
              0,
              50,
              new AdornisFilter({ searchFields: ['firstName', 'lastName', 'email'], search }),
            )({
              email: 1,
              firstName: 1,
              lastName: 1,
              username: 1,
              roles: AdornisRoleToContextsWrapper.allFields,
            }),
          );
          if (!users || !search) return [];
          return users.length ? users : [null];
        }}
      ></tr-dropdown-search-selection>
      ${this._buttons()}
    `;
  }

  protected async addUser(user: User) {
    const action = async () => {
      if (user && !user.isFromNetwork) {
        const existingUser = await User.getUserByEmail(user.email)({ _id: 1 });
        if (!existingUser) {
          const institutionID = this.institutionID;
          if (!institutionID) throw new Error('institutionID is required');
          const userType = this.userTypeForNewlyCreatedUser;
          if (!userType) throw new Error('userType is required');

          const result = await createUserForInstitution(
            institutionID,
            {
              _class: User,
              email: user.email,
              username: user.email,
              ...this.additionalFieldsForUser,
              firstName: user.firstName,
              lastName: user.lastName,
            },
            userType,
          )({ _id: 1 });
          user._id = result._id;
        } else {
          if (this.additionalFieldsForUser.roles) {
            // TODO: this propably onyl works for super-admins
            await Promise.all(
              this.additionalFieldsForUser.roles.map(role => addRoleToUser(user._id, role.name, role.contexts)),
            );
          }
        }
      }
    };

    try {
      await TrDialog.waitFor(action(), this._translation.translate('saving') as string);
      return await this.close(user);
    } catch (e) {
      logger.error({ err: e }, 'Es gab ein Problem beim Anlegen eines neuen Nutzers in einer Veranstaltung');
      return TrDialog.alert(`${this._translation.translate('PROMPT', 'user_couldnt_be_created_error')}`);
    }
  }

  protected _buttons() {
    return html`
      <bh-participation-modify-actions
        @cancel=${() => this.cancel()}
        @save=${() => this._form.validate()}
      ></bh-participation-modify-actions>
    `;
  }

  protected async _switchToEditMode(user: Maybe<User>) {
    if (!this._form.document) throw new Error('should not happen');
    const newUserID = A.getGloballyUniqueID();
    // this._form.document.userID = user?._id ?? newUserID;
    const email = this.lastSearch;
    if (!user) this._form.document = new User({ _id: newUserID, email, username: email });
    else {
      const existingUser = await getByID(
        User,
        user._id,
      )({
        _id: 1,
        _class: 1,
        ...selectionSet(() => User, 2), // TODO: find out why that is needed
        email: 1,
        firstName: 1,
        lastName: 1,
        username: 1,
        roles: {
          name: 1,
          contexts: 1,
        },
        isLdapAccount: 1,
        suspentions: {
          _id: 1,
          _class: 1,
          name: 1,
        },
      });
      if (!existingUser) throw new Error('this user should exist already');
      this._form.document = existingUser;
    }
    this._isEditMode = true;
  }
}
